<template>
  <v-container fluid class="pa-5 seller-page">
    <v-overlay :value="loading" absolute>
      <div class="d-flex flex-column align-center">
        <v-progress-circular indeterminate size="56" width="4" color="#F4811F" />
        <span class="mt-4 white--text font-weight-medium">กำลังโหลดข้อมูล</span>
      </div>
    </v-overlay>

    <!-- Page Header -->
    <div class="page-header mb-6">
      <div class="d-flex align-center" style="gap:14px">
        <div class="header-icon-wrap">
          <v-icon color="#F4811F" size="22">mdi-truck-delivery-outline</v-icon>
        </div>
        <div>
          <h1 class="page-title">ตั้งค่าการจัดส่ง</h1>
          <p class="page-subtitle">กำหนดค่าขนส่งและเงื่อนไขส่งฟรีสำหรับร้านของคุณ</p>
        </div>
      </div>
      <v-btn color="#F4811F" dark class="action-btn px-6" :loading="saving" :disabled="!valid" @click="saveConfig">
        <v-icon left size="18">mdi-content-save-outline</v-icon>บันทึกการตั้งค่า
      </v-btn>
    </div>

    <v-form ref="form" v-model="valid">
      <v-row>
        <!-- ── Left: Form ── -->
        <v-col cols="12" lg="7">

          <!-- Shipping fee card -->
          <div class="s-card mb-5">
            <div class="s-card-header">
              <div class="s-card-icon" style="background:#fff7ed">
                <v-icon color="#F4811F" size="18">mdi-cash-marker</v-icon>
              </div>
              <span class="s-card-title">ค่าจัดส่งปกติ</span>
              <span class="s-card-badge">มาตรฐาน</span>
            </div>
            <div class="s-card-body">
              <div class="field-desc mb-4">
                <v-icon size="15" color="#94a3b8" class="mr-1">mdi-information-outline</v-icon>
                ค่าจัดส่งที่ลูกค้าจะถูกคิดโดยอัตโนมัติเมื่อสั่งซื้อสินค้าจากร้านของคุณ
              </div>
              <div class="field-label">ค่าจัดส่ง (บาท)</div>
              <v-text-field
                v-model.number="config.shippingFee"
                placeholder="0"
                outlined dense
                type="number"
                min="0"
                prepend-inner-icon="mdi-currency-thb"
                :rules="[v => v >= 0 || 'ค่าจัดส่งต้องไม่ติดลบ']"
                class="rounded-lg fee-field"
                background-color="#f9fafb"
              />
              <div class="fee-display" v-if="config.shippingFee > 0">
                <span class="fee-display-label">ลูกค้าจะจ่าย</span>
                <span class="fee-display-amount">฿{{ formatNumber(config.shippingFee) }}</span>
                <span class="fee-display-label">ต่อออเดอร์</span>
              </div>
              <div class="fee-display fee-display--free" v-else>
                <v-icon size="14" color="#16a34a" class="mr-1">mdi-check-circle</v-icon>
                <span style="color:#16a34a;font-weight:600;font-size:13px">ส่งฟรีสำหรับทุกออเดอร์</span>
              </div>
            </div>
          </div>

          <!-- Free shipping card -->
          <div class="s-card mb-6">
            <div class="s-card-header">
              <div class="s-card-icon" style="background:#f0fdf4">
                <v-icon color="#16a34a" size="18">mdi-gift-outline</v-icon>
              </div>
              <span class="s-card-title">โปรโมชันส่งฟรี</span>
              <span class="s-card-badge" v-if="config.freeShippingThreshold > 0" style="background:#dcfce7;color:#15803d">เปิดใช้งาน</span>
              <span class="s-card-badge" v-else>ปิดอยู่</span>
            </div>
            <div class="s-card-body">
              <div class="field-desc mb-4">
                <v-icon size="15" color="#94a3b8" class="mr-1">mdi-information-outline</v-icon>
                เมื่อยอดรวมสินค้าถึงเป้าที่กำหนด ระบบจะปรับค่าจัดส่งเป็น 0 บาทโดยอัตโนมัติ
              </div>
              <div class="field-label">ยอดสั่งซื้อขั้นต่ำเพื่อส่งฟรี (บาท)</div>
              <v-text-field
                v-model.number="config.freeShippingThreshold"
                placeholder="0 = ปิดโปรโมชัน"
                outlined dense
                type="number"
                min="0"
                prepend-inner-icon="mdi-cart-arrow-down"
                :rules="[v => v >= 0 || 'ยอดขั้นต่ำต้องไม่ติดลบ']"
                class="rounded-lg"
                background-color="#f9fafb"
              />
              <div class="threshold-hint" v-if="config.freeShippingThreshold <= 0">
                <v-icon size="14" color="#94a3b8" class="mr-1">mdi-minus-circle-outline</v-icon>
                <span>ตั้งค่าเป็น 0 หมายถึงไม่มีโปรโมชันส่งฟรี</span>
              </div>
            </div>
          </div>

          <!-- Mobile save btn -->
          <div class="d-flex justify-end d-lg-none mb-4">
            <v-btn color="#F4811F" dark large class="action-btn px-8"
              :loading="saving" :disabled="!valid" @click="saveConfig">
              <v-icon left>mdi-content-save-outline</v-icon>บันทึกการตั้งค่า
            </v-btn>
          </div>
        </v-col>

        <!-- ── Right: Live Preview Panel ── -->
        <v-col cols="12" lg="5">

          <!-- Shipping summary visual -->
          <div class="s-card mb-5">
            <div class="s-card-header">
              <div class="s-card-icon" style="background:#eff6ff">
                <v-icon color="#3b82f6" size="18">mdi-eye-outline</v-icon>
              </div>
              <span class="s-card-title">ตัวอย่างที่ลูกค้าจะเห็น</span>
              <span class="preview-live-badge">LIVE</span>
            </div>
            <div class="s-card-body" style="padding:20px">

              <!-- Scenario 1: Normal -->
              <div class="scenario-card scenario-card--normal mb-3">
                <div class="scenario-top">
                  <div class="scenario-icon">
                    <v-icon size="20" color="#F4811F">mdi-shopping-outline</v-icon>
                  </div>
                  <div class="scenario-info">
                    <div class="scenario-title">ออเดอร์ทั่วไป</div>
                    <div class="scenario-sub" v-if="config.freeShippingThreshold > 0">
                      ยอดรวม &lt; ฿{{ formatNumber(config.freeShippingThreshold) }}
                    </div>
                    <div class="scenario-sub" v-else>ทุกออเดอร์</div>
                  </div>
                  <div class="scenario-fee">
                    <template v-if="config.shippingFee > 0">
                      <div class="scenario-fee-amount">฿{{ formatNumber(config.shippingFee) }}</div>
                      <div class="scenario-fee-label">ค่าส่ง</div>
                    </template>
                    <template v-else>
                      <div class="scenario-fee-amount scenario-fee-amount--free">ฟรี</div>
                      <div class="scenario-fee-label">ค่าส่ง</div>
                    </template>
                  </div>
                </div>
              </div>

              <!-- Scenario 2: Free shipping (only when threshold > 0) -->
              <div class="scenario-card scenario-card--free" v-if="config.freeShippingThreshold > 0">
                <div class="scenario-top">
                  <div class="scenario-icon scenario-icon--free">
                    <v-icon size="20" color="#16a34a">mdi-gift-outline</v-icon>
                  </div>
                  <div class="scenario-info">
                    <div class="scenario-title" style="color:#15803d">ครบเกณฑ์ส่งฟรี</div>
                    <div class="scenario-sub">ยอดรวม ≥ ฿{{ formatNumber(config.freeShippingThreshold) }}</div>
                  </div>
                  <div class="scenario-fee">
                    <div class="scenario-fee-amount scenario-fee-amount--free">ฟรี!</div>
                    <div class="scenario-fee-label" style="color:#16a34a">ค่าส่ง</div>
                  </div>
                </div>
                <div class="free-saving-tag">
                  <v-icon size="12" color="#15803d" class="mr-1">mdi-tag-outline</v-icon>
                  ลูกค้าประหยัดได้ ฿{{ formatNumber(config.shippingFee) }}
                </div>
              </div>

              <!-- No promo state -->
              <div class="no-promo-hint" v-if="config.freeShippingThreshold <= 0 && config.shippingFee > 0">
                <v-icon size="14" color="#94a3b8" class="mr-1">mdi-lightbulb-outline</v-icon>
                เพิ่มเกณฑ์ส่งฟรีเพื่อกระตุ้นยอดขาย
              </div>
            </div>
          </div>

          <!-- Progress bar visualization -->
          <div class="s-card mb-5" v-if="config.freeShippingThreshold > 0">
            <div class="s-card-header">
              <div class="s-card-icon" style="background:#fff7ed">
                <v-icon color="#F4811F" size="18">mdi-chart-timeline-variant</v-icon>
              </div>
              <span class="s-card-title">แถบความคืบหน้าลูกค้า</span>
            </div>
            <div class="s-card-body">
              <div class="progress-preview-label mb-1">ตัวอย่างที่ลูกค้าเห็นในตะกร้าสินค้า</div>
              <div class="progress-bar-demo">
                <div class="progress-bar-track">
                  <div class="progress-bar-fill" :style="{ width: previewProgressPct + '%' }"></div>
                </div>
                <div class="progress-bar-info">
                  <span class="progress-bar-amount">฿{{ formatNumber(previewAmount) }}</span>
                  <span class="progress-bar-target">/ ฿{{ formatNumber(config.freeShippingThreshold) }}</span>
                </div>
              </div>
              <div class="progress-cta" v-if="previewProgressPct < 100">
                อีก ฿{{ formatNumber(config.freeShippingThreshold - previewAmount) }} จะได้รับสิทธิ์ <strong>ส่งฟรี!</strong>
              </div>
              <div class="progress-cta progress-cta--done" v-else>
                <v-icon size="14" color="#16a34a" class="mr-1">mdi-check-circle</v-icon>คุณได้รับสิทธิ์ส่งฟรีแล้ว!
              </div>
              <div class="progress-slider-wrap">
                <div class="progress-slider-label">ลองปรับยอดสินค้า:</div>
                <v-slider v-model="previewAmount" :max="config.freeShippingThreshold * 1.2" :min="0" color="#F4811F"
                  track-color="#e2e8f0" hide-details dense thumb-label="always" class="mt-6">
                  <template v-slot:thumb-label="{ value }">฿{{ formatNumber(value) }}</template>
                </v-slider>
              </div>
            </div>
          </div>

          <!-- Tips card -->
          <div class="s-card tips-card">
            <div class="s-card-header">
              <div class="s-card-icon" style="background:#fefce8">
                <v-icon color="#ca8a04" size="18">mdi-lightbulb-outline</v-icon>
              </div>
              <span class="s-card-title">เคล็ดลับการตั้งค่า</span>
            </div>
            <div class="s-card-body" style="padding:16px 20px">
              <div class="tip-item">
                <div class="tip-dot tip-dot--orange"></div>
                <div class="tip-text">ตั้งเกณฑ์ส่งฟรีที่ <strong>1.5–2 เท่า</strong>ของราคาสินค้าเฉลี่ย เพื่อกระตุ้นให้ซื้อเพิ่ม</div>
              </div>
              <div class="tip-item">
                <div class="tip-dot tip-dot--green"></div>
                <div class="tip-text">ค่าส่งที่ <strong>0 บาท</strong> หมายถึงร้านคุณรับผิดชอบค่าขนส่งทั้งหมด</div>
              </div>
              <div class="tip-item">
                <div class="tip-dot tip-dot--blue"></div>
                <div class="tip-text">โปรโมชันส่งฟรีช่วยลด <strong>cart abandonment</strong> ได้อย่างมีนัยสำคัญ</div>
              </div>
            </div>
          </div>
        </v-col>
      </v-row>
    </v-form>
  </v-container>
</template>

<script>
import { getSellerShippingConfigApi, updateSellerShippingConfigApi } from "@/services/api/sellerServices";
import swalAlert from "@/services/alert/swalServices";

export default {
  name: "ShippingConfig",
  data() {
    return {
      valid: true,
      loading: false,
      saving: false,
      config: { shippingFee: 0, freeShippingThreshold: 0 },
      previewAmount: 0,
    };
  },
  computed: {
    previewProgressPct() {
      if (!this.config.freeShippingThreshold) return 0;
      return Math.min(100, Math.round((this.previewAmount / this.config.freeShippingThreshold) * 100));
    },
  },
  watch: {
    "config.freeShippingThreshold"(val) {
      this.previewAmount = Math.round((val || 0) * 0.6);
    },
  },
  async mounted() { await this.fetchConfig(); },
  methods: {
    async fetchConfig() {
      try {
        this.loading = true;
        const res = await getSellerShippingConfigApi();
        if (res.success && res.data) {
          this.config.shippingFee = res.data.shippingFee || 0;
          this.config.freeShippingThreshold = res.data.freeShippingThreshold || 0;
          this.previewAmount = Math.round((this.config.freeShippingThreshold || 0) * 0.6);
        }
      } catch (err) { console.error("Fetch shipping config error:", err); } finally { this.loading = false; }
    },
    async saveConfig() {
      if (!this.$refs.form.validate()) return;
      try {
        this.saving = true;
        const res = await updateSellerShippingConfigApi(this.config);
        if (res.success) swalAlert.Success("สำเร็จ", "บันทึกการตั้งค่าจัดส่งแล้ว");
      } catch (err) {
        swalAlert.Error("บันทึกไม่สำเร็จ", err?.response?.data?.message || "เกิดข้อผิดพลาดในการบันทึกข้อมูล");
      } finally { this.saving = false; }
    },
    formatNumber(val) { return Number(val || 0).toLocaleString("th-TH"); },
  },
};
</script>

<style scoped>
/* ── Page Header Icon ── */
.header-icon-wrap {
  width: 48px; height: 48px;
  background: #fff7ed;
  border-radius: 14px;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
  border: 1px solid #fed7aa;
}

/* ── Field helpers ── */
.field-desc {
  font-size: 12px;
  color: #94a3b8;
  line-height: 1.5;
  display: flex;
  align-items: flex-start;
}

.fee-display {
  display: flex; align-items: center; gap: 6px;
  background: #fff7ed; border-radius: 10px;
  padding: 8px 14px; margin-top: 4px;
}
.fee-display--free { background: #f0fdf4; }
.fee-display-label { font-size: 12px; color: #92400e; }
.fee-display-amount { font-size: 18px; font-weight: 800; color: #F4811F; }

.threshold-hint {
  display: flex; align-items: center;
  font-size: 12px; color: #94a3b8;
  margin-top: 4px;
}

/* ── Live badge ── */
.preview-live-badge {
  font-size: 10px; font-weight: 800; letter-spacing: 1px;
  color: white; background: #22c55e;
  border-radius: 999px; padding: 2px 8px;
  animation: pulse-green 2s infinite;
}
@keyframes pulse-green {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.6; }
}

/* ── Scenario Cards ── */
.scenario-card {
  border-radius: 12px;
  border: 1.5px solid #f1f5f9;
  overflow: hidden;
  transition: border-color 0.2s;
}
.scenario-card--free {
  border-color: #bbf7d0;
  background: #f0fdf4;
}
.scenario-top {
  display: flex; align-items: center; gap: 12px;
  padding: 12px 14px;
}
.scenario-icon {
  width: 40px; height: 40px;
  background: #fff7ed; border-radius: 10px;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.scenario-icon--free { background: #dcfce7; }
.scenario-info { flex: 1; min-width: 0; }
.scenario-title { font-size: 13px; font-weight: 700; color: #1e293b; }
.scenario-sub { font-size: 11px; color: #94a3b8; margin-top: 2px; }
.scenario-fee { text-align: right; flex-shrink: 0; }
.scenario-fee-amount { font-size: 18px; font-weight: 800; color: #F4811F; line-height: 1.1; }
.scenario-fee-amount--free { color: #16a34a; }
.scenario-fee-label { font-size: 10px; color: #94a3b8; }

.free-saving-tag {
  display: flex; align-items: center;
  font-size: 11px; font-weight: 600; color: #15803d;
  background: #dcfce7; padding: 5px 14px;
  border-top: 1px solid #bbf7d0;
}

.no-promo-hint {
  display: flex; align-items: center;
  font-size: 12px; color: #94a3b8;
  background: #f8fafc; border-radius: 8px;
  padding: 10px 12px; margin-top: 8px;
}

/* ── Progress bar demo ── */
.progress-preview-label { font-size: 11px; color: #94a3b8; font-weight: 600; }
.progress-bar-demo { background: #f8fafc; border-radius: 10px; padding: 14px; margin-bottom: 8px; }
.progress-bar-track {
  height: 8px; background: #e2e8f0; border-radius: 999px; overflow: hidden; margin-bottom: 8px;
}
.progress-bar-fill {
  height: 100%; background: linear-gradient(90deg, #F4811F, #fbbf24);
  border-radius: 999px; transition: width 0.4s ease;
}
.progress-bar-info { display: flex; align-items: baseline; gap: 2px; }
.progress-bar-amount { font-size: 15px; font-weight: 800; color: #F4811F; }
.progress-bar-target { font-size: 12px; color: #94a3b8; }
.progress-cta { font-size: 12px; color: #64748b; margin-bottom: 4px; }
.progress-cta--done { color: #16a34a; display: flex; align-items: center; }
.progress-slider-wrap { padding-top: 4px; }
.progress-slider-label { font-size: 11px; color: #94a3b8; font-weight: 600; }

/* ── Tips ── */
.tip-item {
  display: flex; align-items: flex-start; gap: 10px;
  padding: 7px 0;
  border-bottom: 1px solid #f8fafc;
}
.tip-item:last-child { border-bottom: none; }
.tip-dot {
  width: 8px; height: 8px; border-radius: 999px;
  flex-shrink: 0; margin-top: 4px;
}
.tip-dot--orange { background: #F4811F; }
.tip-dot--green  { background: #16a34a; }
.tip-dot--blue   { background: #3b82f6; }
.tip-text { font-size: 12px; color: #64748b; line-height: 1.6; }
.tip-text strong { color: #1e293b; }
</style>
